import { useEffect, useState } from "react";

import {
  type ApiDeps,
  type LlmCostView,
  fetchLlmCost,
} from "../lib/api";
import { t, tn, type Language, type StringKey } from "../lib/i18n";

interface LlmCostPanelProps {
  deps: ApiDeps;
  language: Language;
  /** Shared refresh signal — bump to refetch. Defaults to 0 so the
   * component renders standalone (e.g. in unit tests). */
  reloadKey?: number;
  /** Window length in days from the range picker. Defaults to 30. */
  periodDays?: number;
}

type PanelState =
  | { kind: "loading" }
  | { kind: "loaded"; view: LlmCostView }
  | { kind: "error" };

/** LLM-cost panel (per F37 spec §5.2 row 6). The server decides
 * visibility from the tenant topology; this component renders whatever
 * it's given — the `shown` variant shows the $ breakdown, the `hidden`
 * variant shows an activity narrative with NO dollar figure. It never
 * decides to hide cost locally (that's a server-side security boundary). */
export function LlmCostPanel({
  deps,
  language,
  reloadKey = 0,
  periodDays = 30,
}: LlmCostPanelProps): JSX.Element {
  const [state, setState] = useState<PanelState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetchLlmCost(deps, periodDays)
      .then((view) => {
        if (!cancelled) setState({ kind: "loaded", view });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, reloadKey, periodDays]);

  return (
    <section
      aria-label={t("cost.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <h2 className="mb-3 text-base font-semibold text-kwanza-ink">
        {t("cost.heading", language)}
      </h2>
      {state.kind === "loading" ? (
        <div
          className="h-20 animate-pulse rounded bg-kwanza-divider"
          aria-busy="true"
          data-testid="cost-skeleton"
        />
      ) : state.kind === "error" ? (
        <p className="text-sm text-kwanza-alert" data-testid="cost-error">
          {t("error.fetch_failed.body", language)}
        </p>
      ) : state.view.visibility === "hidden" ? (
        <CostNarrative view={state.view} language={language} />
      ) : (
        <CostBreakdown view={state.view} language={language} />
      )}
    </section>
  );
}

function CostNarrative({
  view,
  language,
}: {
  view: LlmCostView;
  language: Language;
}): JSX.Element {
  return (
    <div data-testid="cost-hidden">
      <p className="text-sm font-medium text-kwanza-ink">
        {t("cost.hidden.title", language)}
      </p>
      <p className="text-sm text-kwanza-inkSoft">
        {t("cost.hidden.body", language)}
      </p>
      <p className="mt-3 text-xs uppercase tracking-wide text-kwanza-inkSoft">
        {t("cost.activity.heading", language)}
      </p>
      <ul className="mt-1 flex gap-6 text-sm text-kwanza-ink">
        <li>
          <span className="font-semibold">{view.invoices_processed ?? 0}</span>{" "}
          {t("cost.activity.invoices", language)}
        </li>
        <li>
          <span className="font-semibold">
            {(view.hours_saved ?? 0).toFixed(1)}
          </span>{" "}
          {t("cost.activity.hours", language)}
        </li>
      </ul>
    </div>
  );
}

/** Resolve a raw registry action name to a friendly, operations-grade
 * label. The whole `memory_consolidation_*` family collapses to the single
 * "Manutenção do agente" umbrella (an internal-maintenance cost the persona
 * shouldn't see itemised by lifecycle verb); known LLM call sites get their
 * `cost.action.<key>` noun; anything unmapped is humanised
 * (`some_action` → "Some action") so a raw snake_case verb never renders. */
function costActionLabel(action: string, language: Language): string {
  if (action.startsWith("memory_consolidation")) {
    return t("cost.action.maintenance", language);
  }
  try {
    return t(`cost.action.${action}` as StringKey, language);
  } catch {
    const spaced = action.replace(/_/g, " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
}

function CostBreakdown({
  view,
  language,
}: {
  view: LlmCostView;
  language: Language;
}): JSX.Element {
  const total = view.total_usd ?? 0;
  const byAction = view.by_action ?? {};
  // Map raw action keys to friendly labels, then aggregate by label so the
  // collapsed `memory_consolidation_*` family renders as one row, not many.
  const byLabel = new Map<string, number>();
  for (const [action, amount] of Object.entries(byAction)) {
    const label = costActionLabel(action, language);
    byLabel.set(label, (byLabel.get(label) ?? 0) + amount);
  }
  const rows = [...byLabel.entries()].sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="cost-shown">
      <div className="text-sm uppercase tracking-wide text-kwanza-inkSoft">
        {t("cost.total", language)}
      </div>
      <div className="mt-1 text-3xl font-semibold text-kwanza-ink">
        ${total.toFixed(2)}
      </div>
      {/* Cost-vs-value line — ties the spend to what it bought so the panel
       * answers "its value", not just "its cost". */}
      <p className="mt-1 text-xs text-kwanza-inkSoft" data-testid="cost-value-line">
        {tn(
          "cost.value_line",
          {
            n: view.invoices_processed ?? 0,
            h: (view.hours_saved ?? 0).toFixed(1),
          },
          language,
        )}
      </p>
      {view.advisory ? (
        <p className="mt-1 text-xs text-kwanza-inkSoft" data-testid="cost-advisory">
          {t("cost.advisory", language)}
        </p>
      ) : null}
      {rows.length > 0 ? (
        // `reflow-card` reflows to stacked cards below `sm` (tailwind.css);
        // the `display` override strips implicit table roles, so explicit
        // role="table/rowgroup/row/columnheader/cell" restore them (A8 also
        // added the real <thead>/<th> the table previously lacked).
        // jsx-a11y/no-redundant-roles is scoped off for this file in .eslintrc.
        <table role="table" className="reflow-card mt-3 w-full text-sm">
          <caption className="sr-only">{t("cost.by_action", language)}</caption>
          <thead role="rowgroup">
            <tr role="row" className="text-left text-xs uppercase tracking-wide text-kwanza-inkSoft">
              <th role="columnheader" scope="col" className="py-1 font-medium">
                {t("cost.col.action", language)}
              </th>
              <th role="columnheader" scope="col" className="py-1 text-right font-medium">
                {t("cost.col.amount", language)}
              </th>
            </tr>
          </thead>
          <tbody role="rowgroup" className="divide-y divide-kwanza-divider">
            {rows.map(([label, amount]) => (
              <tr role="row" key={label} data-testid="cost-action-row">
                <td
                  role="cell"
                  data-label={t("cost.col.action", language)}
                  className="py-1 text-kwanza-inkSoft"
                >
                  {label}
                </td>
                <td
                  role="cell"
                  data-label={t("cost.col.amount", language)}
                  className="py-1 text-right text-kwanza-ink"
                >
                  ${amount.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {view.calls_with_cost_error ? (
        <p className="mt-2 text-xs text-kwanza-inkSoft">
          {t("cost.cost_errors", language)}: {view.calls_with_cost_error}
        </p>
      ) : null}
    </div>
  );
}
